/**
 * GlobalSearch — cmd+K / ctrl+K driven global search across members
 * and categories. Uses `/api/search` (see backend/routes/search.py).
 *
 * UX contract:
 *  - Slim search button always visible in the top nav (data-testid="global-search-btn").
 *  - Keyboard shortcut: ⌘K / Ctrl+K → opens dialog (data-testid="global-search-dialog").
 *  - Input debounces 300ms; queries under 2 chars are not sent (matches
 *    server's minimum-length guard).
 *  - Three grouped result sets: Members, Categories, "Members in <cat>".
 *  - Arrow-up / arrow-down navigate rows; Enter activates the highlighted row.
 *  - Clicking a member opens their DM (`/messages?dm=<id>`).
 *  - Clicking a category deep-links to the Leads page filtered by that
 *    category (`/leads?category=<name>`).
 *  - Clicking a preview member (inside a category card) opens their DM AND
 *    remembers the category context in a toast, so the caller knows why
 *    they were matched.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Tag, ArrowRight, Loader2, X, Command } from 'lucide-react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import api from '@/lib/api';
import logger from '@/lib/logger';
import { MemberAvatar } from '@/components/MemberAvatar';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

/** Detect Mac/⌘ so we can render the right shortcut hint. */
function useIsMac() {
  return useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || '');
  }, []);
}

/** Convert a category name to a safe CSS-selector-compatible testid slug. */
const slugify = (s) => (s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'unnamed';

/** Flatten the API's three groups into one array so arrow keys work. */
function flattenResults(data) {
  if (!data) return [];
  const flat = [];
  (data.members || []).forEach((m) => flat.push({ kind: 'member', item: m }));
  (data.categories || []).forEach((c) => flat.push({ kind: 'category', item: c }));
  const catCtx = data.matched_category || data.query;
  (data.members_in_category || []).forEach((m) => flat.push({ kind: 'category_member', item: m, category: catCtx }));
  return flat;
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debouncedRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const isMac = useIsMac();

  // ⌘K / Ctrl+K + `/` global hotkeys
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      const inField = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) ||
                      document.activeElement?.isContentEditable;
      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === '/' && !inField && !open) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    if (debouncedRef.current) clearTimeout(debouncedRef.current);
    if (q.trim().length < MIN_QUERY_LEN) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debouncedRef.current = setTimeout(async () => {
      try {
        const res = await api.search(q.trim(), 8);
        setData(res.data);
        setActiveIndex(0);
      } catch (err) {
        logger.error(err);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => debouncedRef.current && clearTimeout(debouncedRef.current);
  }, [q, open]);

  // Focus input when opening
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(''); setData(null); setActiveIndex(0); }
  }, [open]);

  const flat = flattenResults(data);

  const openMemberDM = (m, categoryContext) => {
    setOpen(false);
    if (categoryContext) {
      toast.message(`Connecting via ${categoryContext}`, {
        description: `${m.name || m.email} · from your search`,
      });
    }
    navigate(`/messages?dm=${encodeURIComponent(m.id)}`);
  };

  const openCategory = (cat) => {
    setOpen(false);
    navigate(`/leads?category=${encodeURIComponent(cat.name)}`);
  };

  const activate = (row) => {
    if (!row) return;
    if (row.kind === 'member') openMemberDM(row.item);
    else if (row.kind === 'category') openCategory(row.item);
    else if (row.kind === 'category_member') openMemberDM(row.item, row.category);
  };

  const onKeyDown = (e) => {
    if (!flat.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(flat[activeIndex]);
    }
  };

  const showEmptyState = q.trim().length >= MIN_QUERY_LEN && !loading && data && flat.length === 0;
  const showHint = q.trim().length < MIN_QUERY_LEN;

  const rowIndexOf = (kind, itemId) => flat.findIndex(
    (r) => r.kind === kind && (r.item.id === itemId || r.item.name === itemId),
  );

  return (
    <>
      {/* Trigger — small pill button, cmd+K hint */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="global-search-btn"
        className="hidden md:flex items-center gap-2 px-3 h-8 rounded-sm text-xs text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors"
        aria-label="Open global search"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Search members &amp; categories…</span>
        <kbd className="ml-2 hidden lg:flex items-center gap-0.5 text-[9px] font-mono font-medium text-slate-400 bg-white border border-slate-200 rounded-sm px-1 py-0.5">
          {isMac ? <Command className="w-2.5 h-2.5" /> : 'Ctrl'}
          <span>K</span>
        </kbd>
      </button>
      {/* Mobile-only icon button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="global-search-btn-mobile"
        className="md:hidden p-2 rounded-sm hover:bg-slate-50 transition-colors"
        aria-label="Open global search"
      >
        <Search className="w-4 h-4 text-slate-600" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="p-0 max-w-2xl border-slate-200 rounded-sm gap-0 overflow-hidden"
          data-testid="global-search-dialog"
        >
          <DialogTitle className="sr-only">Global search</DialogTitle>
          <DialogDescription className="sr-only">
            Search across members and categories. Use arrow keys to navigate
            and Enter to open. Press Escape to close.
          </DialogDescription>
          {/* Input bar */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search members, categories, sub-categories…"
              className="flex-1 outline-none text-sm text-slate-900 placeholder:text-slate-400 bg-transparent"
              data-testid="global-search-input"
              autoComplete="off"
              spellCheck={false}
            />
            {loading ? (
              <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
            ) : q ? (
              <button
                onClick={() => { setQ(''); inputRef.current?.focus(); }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {showHint && (
              <div className="px-6 py-10 text-center" data-testid="global-search-hint">
                <p className="text-sm text-slate-500 mb-1">Type at least 2 characters</p>
                <p className="text-[11px] text-slate-400">
                  Members · Categories · Sub-categories · Members in a category
                </p>
              </div>
            )}

            {showEmptyState && (
              <div className="px-6 py-10 text-center" data-testid="global-search-empty">
                <p className="text-sm text-slate-500 mb-1">No results for <span className="font-semibold text-slate-700">&quot;{q}&quot;</span></p>
                <p className="text-[11px] text-slate-400">Try a shorter query or a different keyword.</p>
              </div>
            )}

            {data && flat.length > 0 && (
              <div className="py-2">
                {/* MEMBERS */}
                {data.members?.length > 0 && (
                  <div className="mb-1" data-testid="global-search-members-group">
                    <p className="px-4 py-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold flex items-center gap-1.5">
                      <Users className="w-3 h-3" /> Members ({data.members.length})
                    </p>
                    {data.members.map((m) => {
                      const idx = rowIndexOf('member', m.id);
                      const active = idx === activeIndex;
                      return (
                        <button
                          key={`m-${m.id}`}
                          onClick={() => openMemberDM(m)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          data-testid={`search-member-${m.id}`}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${active ? 'bg-[#EFF6FF]' : 'hover:bg-slate-50'}`}
                        >
                          <MemberAvatar userId={m.id} name={m.name} size="sm" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900 font-medium truncate">
                              {m.name || <span className="text-slate-400 italic">Unnamed</span>}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">
                              {m.email}{m.company ? ` · ${m.company}` : ''}
                            </p>
                          </div>
                          {m.role && m.role !== 'member' && (
                            <span className="text-[9px] uppercase tracking-[0.15em] font-medium text-[#0047AB] bg-blue-50 border border-blue-100 rounded-sm px-1.5 py-0.5">
                              {m.role}
                            </span>
                          )}
                          <ArrowRight className={`w-3.5 h-3.5 transition-opacity ${active ? 'text-[#0047AB] opacity-100' : 'text-slate-300 opacity-0'}`} />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* CATEGORIES */}
                {data.categories?.length > 0 && (
                  <div className="mb-1" data-testid="global-search-categories-group">
                    <p className="px-4 py-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold flex items-center gap-1.5">
                      <Tag className="w-3 h-3" /> Categories ({data.categories.length})
                    </p>
                    {data.categories.map((c) => {
                      const idx = rowIndexOf('category', c.name);
                      const active = idx === activeIndex;
                      return (
                        <button
                          key={`c-${c.name}`}
                          onClick={() => openCategory(c)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          data-testid={`search-category-${slugify(c.name)}`}
                          className={`w-full flex items-start gap-3 px-4 py-2 text-left transition-colors ${active ? 'bg-[#EFF6FF]' : 'hover:bg-slate-50'}`}
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-sm bg-[#EFF6FF] border border-[#0047AB]/20 flex items-center justify-center">
                            <Tag className="w-3.5 h-3.5 text-[#0047AB]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900 font-medium truncate">{c.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] text-slate-500">
                                {c.member_count} member{c.member_count === 1 ? '' : 's'} active
                              </span>
                              {c.matched_on === 'sub_category' && c.matched_sub_categories?.length > 0 && (
                                <span className="text-[10px] text-slate-400 italic truncate">
                                  matched in {c.matched_sub_categories.slice(0, 2).join(', ')}
                                </span>
                              )}
                            </div>
                            {c.preview_members?.length > 0 && (
                              <div className="flex items-center gap-1.5 mt-1.5 -space-x-2">
                                {c.preview_members.slice(0, 5).map((p) => (
                                  <div
                                    key={p.id}
                                    onClick={(e) => { e.stopPropagation(); openMemberDM(p, c.name); }}
                                    className="cursor-pointer relative ring-2 ring-white rounded-full hover:z-10"
                                    title={`Chat with ${p.name || p.email}`}
                                    data-testid={`search-cat-preview-${p.id}`}
                                  >
                                    <MemberAvatar userId={p.id} name={p.name} size="xs" />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <ArrowRight className={`w-3.5 h-3.5 mt-2 transition-opacity ${active ? 'text-[#0047AB] opacity-100' : 'text-slate-300 opacity-0'}`} />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* MEMBERS IN CATEGORY (exact match only) */}
                {data.members_in_category?.length > 0 && (
                  <div className="mb-1 border-t border-slate-100 mt-1 pt-1" data-testid="global-search-cat-members-group">
                    <p className="px-4 py-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold flex items-center gap-1.5">
                      <Users className="w-3 h-3" /> Members in &quot;{data.matched_category || data.query}&quot; ({data.members_in_category.length})
                    </p>
                    {data.members_in_category.map((m) => {
                      const idx = rowIndexOf('category_member', m.id);
                      const active = idx === activeIndex;
                      return (
                        <button
                          key={`cm-${m.id}`}
                          onClick={() => openMemberDM(m, data.matched_category || data.query)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          data-testid={`search-cat-member-${m.id}`}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${active ? 'bg-[#EFF6FF]' : 'hover:bg-slate-50'}`}
                        >
                          <MemberAvatar userId={m.id} name={m.name} size="sm" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900 font-medium truncate">
                              {m.name || <span className="text-slate-400 italic">Unnamed</span>}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">
                              {m.email}{m.company ? ` · ${m.company}` : ''}
                            </p>
                          </div>
                          <span className="text-[9px] uppercase tracking-[0.15em] font-medium text-slate-500 whitespace-nowrap">Connect →</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer with hotkey hints */}
          <div className="flex items-center justify-between gap-4 px-4 py-2 border-t border-slate-200 bg-slate-50/50 text-[10px] text-slate-500 font-mono">
            <div className="flex items-center gap-3">
              <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded-[3px]">↑↓</kbd> navigate</span>
              <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded-[3px]">↵</kbd> open</span>
              <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded-[3px]">esc</kbd> close</span>
            </div>
            <span className="opacity-70">Visibility follows your team &amp; sharing rules</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
